import { RefObject, useCallback, useEffect, useState } from 'react';

import { useRecoilValue } from 'recoil';

import {
  createGiftOrder,
  deleteGiftOrderById,
  sendGiftVBankAlimUser,
  verifyGiftOrderById,
} from '@shared/api/giftOrder/giftOrder.controller';
import { GiftOrderCreateDto } from '@shared/api/giftOrder/giftOrder.interface';
import { User } from '@shared/api/user/user.interface';
import { CheckOutState } from '@shared/interfaces/bootpay.interface';
import { notificateError } from '@shared/plugIn/ant-notification/ant-notifiaction';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { UserRole } from '@shared/types';
import _ from 'lodash';
import { useNavigate } from 'react-router-dom';

import { getGiftBootpayRequestPaymentDto } from '../utils/getGiftBootpayRequestDto';
import { scrollToInvalidField } from '../utils/scrollToInvalidField';

declare global {
  interface Window {
    Bootpay: any;
  }
}

interface UseGiftCheckoutPaymentParams {
  /** GiftOrderCreateDto를 생성하는 콜백. null 반환 시 결제 진행 안함 */
  createOrderDto: () => GiftOrderCreateDto | null;
  /** 결제 성공 후 추가 처리 (예: 장바구니 비우기) */
  onPaymentSuccess?: () => void;
  /** bugoId (리다이렉트용) */
  bugoId: string | null;
  /** 주문자 이름 */
  receiverName: string;
  /** 주문자 연락처 */
  receiverTel: string;
  /** 결제 가능 여부 (상품이 있는지) */
  hasItems: boolean;
  /** Bulk 배송 시 배송지 유효성 검증 */
  validateDeliveryAddress?: () => boolean;
  /** 주문자 이름 입력란 ref — 미입력 alert 시 해당 위치로 스크롤 */
  receiverNameRef?: RefObject<HTMLInputElement>;
  /** 주문자 연락처 입력란 ref — 미입력 alert 시 해당 위치로 스크롤 */
  receiverTelRef?: RefObject<HTMLInputElement>;
}

interface UseGiftCheckoutPaymentResult {
  /** 현재 결제 상태 */
  checkoutState: CheckOutState;
  /** 결제하기 버튼 클릭 핸들러 */
  handlePayment: () => Promise<void>;
  /** 결제 진행 중 여부 */
  isProcessing: boolean;
}

/**
 * Gift 결제 공통 로직을 처리하는 커스텀 훅
 * - Bootpay 결제 요청
 * - 결제 완료/실패 처리
 * - 가상계좌 처리
 * - Guest 사용자 처리
 */
export const useGiftCheckoutPayment = ({
  createOrderDto,
  onPaymentSuccess,
  bugoId,
  receiverName,
  receiverTel,
  hasItems,
  validateDeliveryAddress,
  receiverNameRef,
  receiverTelRef,
}: UseGiftCheckoutPaymentParams): UseGiftCheckoutPaymentResult => {
  const navigate = useNavigate();
  const notificationInstance = useRecoilValue(notificationInstanceAtom);

  // Auth
  const { storeRole, userProfile, signupOrLoginStoreGuest } = useAuth();

  // 결제 상태
  const [checkoutState, setCheckoutState] = useState(CheckOutState.IDLE);
  const [giftOrderIdState, setGiftOrderIdState] = useState<string | undefined>();
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [requestState, setRequestState] = useState<boolean>(false);

  // 결제 성공 시 리다이렉트
  useEffect(() => {
    if (checkoutState === CheckOutState.DONE && !_.isNil(giftOrderIdState)) {
      onPaymentSuccess?.();
      navigate(
        `/gift-store/order/${giftOrderIdState}/payment?success=${isSuccess}&bugoId=${bugoId}`,
      );
    }
    if (checkoutState === CheckOutState.VBANK && !_.isNil(giftOrderIdState)) {
      navigate(
        `/gift-store/order/${giftOrderIdState}/payment?success=${isSuccess}&bugoId=${bugoId}`,
      );
    }
  }, [navigate, checkoutState, giftOrderIdState, isSuccess, bugoId, onPaymentSuccess]);

  // 결제 완료 처리
  const doneHandle = useCallback(
    async (data: any, giftOrderId: string) => {
      try {
        const isValid = await verifyGiftOrderById(
          giftOrderId,
          storeRole(),
          data.receipt_id,
        );

        if (isValid) {
          setIsSuccess(true);
          setGiftOrderIdState(giftOrderId);
          setCheckoutState(CheckOutState.DONE);
        } else {
          throw Error('유효하지 않은 결제 시도로 인한 취소 처리');
        }
      } catch (err: any) {
        console.error(err);
        setCheckoutState(CheckOutState.IDLE);
        notificateError(
          notificationInstance,
          `결제 실패\n${err.response?.data?.message}`,
        );
      }
    },
    [notificationInstance, storeRole],
  );

  // 가상계좌 처리 — vbank_data가 누락돼도 throw하지 않도록 방어
  const readyHandle = useCallback(async (data: any, giftOrderId: string) => {
    const { bank_account, bank_name } = data?.vbank_data ?? {};
    const vBank = bank_name && bank_account ? `${bank_name}\n${bank_account}` : '';
    if (vBank) {
      await sendGiftVBankAlimUser(giftOrderId, vBank);
      setIsSuccess(true);
    }
    // 계좌정보 유무와 무관하게 주문은 발급됐으므로 결과 페이지로 이동
    setGiftOrderIdState(giftOrderId);
    setCheckoutState(CheckOutState.VBANK);
  }, []);

  // Bootpay 결제 요청
  const onBootpayRequest = useCallback(async () => {
    if (_.isNil(userProfile)) {
      return;
    }

    const giftOrderDto = createOrderDto();
    if (!giftOrderDto) {
      return;
    }

    try {
      // 1. Backend에 GiftOrder 생성
      const { data: giftOrder } = await createGiftOrder(giftOrderDto, storeRole());

      try {
        // 2. 결제 중 상태로 변경
        setCheckoutState(CheckOutState.DOING);

        // 3. Bootpay 결제 요청
        const paymentResponse = await window.Bootpay.requestPayment(
          getGiftBootpayRequestPaymentDto({
            giftOrder,
            userProfile: userProfile as User<string>,
          }),
        );

        let confirmedData;
        let enable = true;

        switch (paymentResponse.event) {
          case 'issued':
            // 가상계좌 입금 완료 처리
            await readyHandle(paymentResponse.data, giftOrder._id);
            break;
          case 'done':
            // 결제 완료
            await doneHandle(paymentResponse.data, giftOrder._id);
            break;
          case 'confirm':
            enable = true;
            if (enable) {
              confirmedData = await window.Bootpay.confirm();
              if (confirmedData.event === 'done') {
                // 결제 성공
              } else if (confirmedData.event === 'error') {
                // 결제 승인 실패
              }
            } else {
              window.Bootpay.destroy();
            }
            break;
          case 'close':
            // 결제창 닫힘
            break;
        }
      } catch (error) {
        // 결제 실패 처리
        switch ((error as any).event) {
          case 'cancel':
            // 사용자가 결제창을 닫음
            console.error(error);
            setCheckoutState(CheckOutState.IDLE);
            await deleteGiftOrderById(giftOrder._id, storeRole());
            break;
          case 'error':
            // 결제 승인 중 오류
            console.error(error);
            setGiftOrderIdState(giftOrder._id);
            setCheckoutState(CheckOutState.DONE);
            break;
          default:
            // 결제창 미응답·스크립트 로드 실패 등 예기치 못한 오류 — '결제 중' 멈춤 방지
            console.error(error);
            setCheckoutState(CheckOutState.IDLE);
            notificateError(
              notificationInstance,
              '결제를 진행하지 못했습니다. 잠시 후 다시 시도해주세요.',
            );
            break;
        }
      }
    } catch (err) {
      // GiftOrder 생성 실패 등 — 사용자에게 알리고 상태 복구
      console.error(err);
      setCheckoutState(CheckOutState.IDLE);
      notificateError(
        notificationInstance,
        '주문 생성에 실패했습니다. 잠시 후 다시 시도해주세요.',
      );
    }
  }, [
    userProfile,
    createOrderDto,
    storeRole,
    readyHandle,
    doneHandle,
    notificationInstance,
  ]);

  // 결제 요청 트리거
  useEffect(() => {
    if (requestState) {
      onBootpayRequest();
      setRequestState(false);
    }
  }, [requestState, onBootpayRequest]);

  // 결제하기 버튼 클릭
  const handlePayment = useCallback(async () => {
    if (!receiverName.trim()) {
      alert('주문자 이름을 입력해주세요.');
      scrollToInvalidField(receiverNameRef?.current);
      return;
    }
    if (!receiverTel.trim()) {
      alert('주문자 연락처를 입력해주세요.');
      scrollToInvalidField(receiverTelRef?.current);
      return;
    }
    if (!hasItems) {
      alert('결제할 상품이 없습니다.');
      return;
    }
    if (validateDeliveryAddress && !validateDeliveryAddress()) {
      return;
    }

    // Guest 사용자 처리
    if (_.isNil(userProfile) || userProfile.roles.includes(UserRole.StoreGuest)) {
      try {
        const guestSignupData = {
          name: receiverName,
          phoneNumber: receiverTel,
          password: String(receiverTel).split('').slice(-4).join(''),
        };
        await signupOrLoginStoreGuest(guestSignupData, true);
        setRequestState(true);
      } catch (err) {
        console.error(err);
      }
    } else {
      setRequestState(true);
    }
  }, [
    receiverName,
    receiverTel,
    hasItems,
    validateDeliveryAddress,
    receiverNameRef,
    receiverTelRef,
    userProfile,
    signupOrLoginStoreGuest,
  ]);

  return {
    checkoutState,
    handlePayment,
    isProcessing: checkoutState === CheckOutState.DOING,
  };
};
